/**
 * Data Review API Service
 *
 * Handles API calls for Catalog Management Data Review functionality.
 * Includes stats, products list, and unmapped products APIs.
 */
import AuthManager from "Common/AuthManager";
import ServiceWrapper from "Services/ServiceWrapper";
import AppSettings from "Settings/AppSettings";
import { APIProduct } from "Pages/GMS/CatalogManagement/types/api.types";

const API_BASE_URL = AppSettings.GMS_BACKEND_API_URL;

// ============================================================================
// Types
// ============================================================================

export interface DataReviewStatsResponse {
  done: boolean;
  data: {
    tabs: {
      sku_matched: number;
      mpn_upc_matched: number;
      unmatched: number;
      ignored_products: number;
      ignored_updates: number;
      processed: number;
    };
    sub_tabs: {
      "mpn_upc_matched/unmapped_sku": number;
      "mpn_upc_matched/unmapped_sku_with_updates": number;
    };
  };
}

export interface DataReviewProductsParams {
  tab: string;
  sub_tab?: string;
  status?: string;
  search?: string;
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export interface DataReviewProductsResponse {
  done: boolean;
  data: APIProduct[];
  page: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}


export interface IncompleteMappingCountResponse {
  done: boolean;
  data: {
    incomplete_count: number;
    by_status: {
      partial: number;
      unmapped: number;
      pending: number;
      other: number;
    };
    complete_count: number;
    total_in_staging: number;
  };
}

export interface UnmappedProductsParams {
  page?: number;
  limit?: number;
  mapping_status?: "partial" | "unmapped";
  search?: string;
}

export interface UnmappedProductsResponse {
  done: boolean;
  product_data: UnmappedProduct[];
  page: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface UnmappedProduct {
  _id: string;
  cs_key: string;
  sku: string;
  prod: {
    name: string;
    model_number?: string;
    [key: string]: string | undefined;
  };
  mapped_category?: string;
  unmapped_attributes_count: number;
  unmapped_attribute_values_count: number;
  unmapped_attributes: string[];
  attributes_with_unmapped_values: {
    attribute: string;
    unmapped_values: string[];
  }[];
}

// ============================================================================
// Bulk Action Types
// ============================================================================

export type BulkActionType =
  | "update"
  | "ignore"
  | "map_sku"
  | "map_override"
  | "create_new"
  | "restore";

export interface BulkActionParams {
  skus: string[];
  action: BulkActionType;
  reason?: string;
  target_gms_id?: string;
  // Active tab the dispatch is coming from. BE writes it on the queue
  // row at enqueue time so the initial "Queued action … from 'X'" log
  // line carries the source from the first write instead of waiting
  // for the worker to enrich it ~1s later.
  source_tab?: string;
}

// Whole-tab bulk actions (ticket 13765): "Update All" (SKU Matched) and
// "Create All" (Unmatched). Only these two have a whole-tab semantic — the
// server maps the action to its tab (update → sku_matched, create_new →
// unmatched) and acts on every row present at click time.
export type BulkAllActionType = "update" | "create_new";

export interface BulkAllParams {
  action: BulkAllActionType;
  // Optional cross-check — the BE 400s if it doesn't match the action's tab.
  source_tab?: string;
}

export interface BulkActionAsyncResponse {
  done: boolean;
  // Queue entry uid — the canonical identifier for polling status via
  // GET /queue/<uid>. The BE handler no longer waits for the celery
  // task_id before returning (the worker stamps it on the queue row
  // when it claims the task), so task_id is no longer in the response.
  queue_uid: string;
  message: string;
  total: number;
  // Kept optional for backward-compat with any callers that haven't
  // migrated to queue_uid yet — current BE omits it.
  task_id?: string;
}

export interface BulkActionResult {
  done: boolean;
  success_count: number;
  failed_count: number;
  failed: Array<{ error: string; skus: string[] }>;
  processing: boolean;
  error?: boolean;
  message?: string;
}

// ============================================================================
// Bulk Edit Changes Types
// ============================================================================

export interface ProductEditSourceData {
  prod: {
    name?: string;
    description?: string;
    price?: string;
    upc?: string;
    model_number?: string;
    status?: string;
    version?: string;
    [key: string]: string | undefined;
  };
  real_sku?: string;
  parent_id?: string;
  category?: string;
  category_id?: string;
  category_type?: string;
  category_type_id?: string;
  attributes?: Array<{
    name: string;
    values: string[];
  }>;
}

export interface ProductEdit {
  sku: string;
  source_data: ProductEditSourceData;
}

export interface BulkEditChangesParams {
  edits: ProductEdit[];
}

export interface BulkEditChangesResponse {
  done: boolean;
  message: string;
  success_count: number;
  failed_count: number;
  failed: Array<{
    sku: string;
    error: string;
  }>;
}

// ============================================================================
// Search & Replace Types
// ============================================================================

export interface SearchProductsParams {
  q: string;
  industry_id?: string;
  limit?: number;
}

export interface SearchProductResult {
  gms_id: string;
  name: string;
  category?: string;
  category_id?: string;
  model_number?: string;
  upc?: string;
  price?: string;
  product_lifecycle_status?: string;
  description?: string;
  version?: string;
  sku?: string;
  real_sku?: string;
  parent_id?: string;
  attributes?: { name: string; values: string[] }[];
}

export interface SearchProductsResponse {
  done: boolean;
  data: SearchProductResult[];
  count: number;
}

export interface ReplaceProductParams {
  new_gms_id: string;
  reason?: string;
}

export interface ReplaceProductResponse {
  done: boolean;
  message: string;
  original_gms_id?: string;
  new_gms_id?: string;
}

export interface RevertReplacementResponse {
  done: boolean;
  message: string;
  original_gms_id?: string;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get Data Review Stats
 *
 * Fetches tab counts for badge display on the Data Review page.
 *
 * @param csKey - Channel source key
 * @returns Stats response with tab counts
 */
export async function getDataReviewStats(
  csKey: string
): Promise<DataReviewStatsResponse> {
  try {
    if (!csKey) {
      throw new Error("Channel key is required");
    }

    const url = `${API_BASE_URL}catalogue/data-review/${csKey}/stats`;
    const response = await ServiceWrapper.doGet(
      url,
      null,
      null,
      AuthManager.getGmsToken()
    );

    if (response?.errorfound) {
      throw new Error(
        response?.data?.message || "Failed to fetch data review stats"
      );
    }

    return response.data;
  } catch (error: any) {
    throw error;
  }
}

/**
 * Get Data Review Products
 *
 * Fetches products list for a specific tab with pagination and filtering.
 *
 * @param csKey - Channel source key
 * @param params - Query parameters (tab, sub_tab, status, search, page, per_page, sort_by, sort_order)
 * @returns Products list with pagination info
 */
export async function getDataReviewProducts(
  csKey: string,
  params: DataReviewProductsParams
): Promise<DataReviewProductsResponse> {
  try {
    if (!csKey) {
      throw new Error("Channel key is required");
    }

    if (!params.tab) {
      throw new Error("Tab parameter is required");
    }

    const queryParams = new URLSearchParams();
    queryParams.append("tab", params.tab);

    if (params.sub_tab) {
      queryParams.append("sub_tab", params.sub_tab);
    }
    if (params.status) {
      queryParams.append("status", params.status);
    }
    if (params.search) {
      queryParams.append("search", params.search);
    }
    if (params.page !== undefined) {
      queryParams.append("page", String(params.page));
    }
    if (params.per_page !== undefined) {
      queryParams.append("per_page", String(params.per_page));
    }
    if (params.sort_by) {
      queryParams.append("sort_by", params.sort_by);
    }
    if (params.sort_order) {
      queryParams.append("sort_order", params.sort_order);
    }

    const url = `${API_BASE_URL}catalogue/data-review/${csKey}/products?${queryParams.toString()}`;
    const response = await ServiceWrapper.doGet(
      url,
      null,
      null,
      AuthManager.getGmsToken()
    );

    if (response?.errorfound) {
      throw new Error(
        response?.data?.message || "Failed to fetch data review products"
      );
    }

    return response.data;
  } catch (error: any) {
    throw error;
  }
}

/**
 * Get Incomplete Mapping Count
 *
 * Fetches count of products with incomplete mappings for the "Missing Mapping" button badge.
 *
 * @param csKey - Channel source key
 * @returns Count response with breakdown by status
 */
export async function getIncompleteMappingCount(
  csKey: string
): Promise<IncompleteMappingCountResponse> {
  try {
    if (!csKey) {
      throw new Error("Channel key is required");
    }

    const url = `${API_BASE_URL}catalogue/data-review/${csKey}/incomplete-mapping-count`;
    const response = await ServiceWrapper.doGet(
      url,
      null,
      null,
      AuthManager.getGmsToken()
    );

    if (response?.errorfound) {
      throw new Error(
        response?.data?.message || "Failed to fetch incomplete mapping count"
      );
    }

    return response.data;
  } catch (error: any) {
    throw error;
  }
}

/**
 * Get Unmapped Products List
 *
 * Fetches products needing mapping for the Missing Mapping view.
 *
 * @param csKey - Channel source key
 * @param params - Query parameters (page, limit, mapping_status)
 * @returns Unmapped products list with pagination
 */
export async function getUnmappedProducts(
  csKey: string,
  params?: UnmappedProductsParams
): Promise<UnmappedProductsResponse> {
  try {
    if (!csKey) {
      throw new Error("Channel key is required");
    }

    const queryParams = new URLSearchParams();

    if (params?.page !== undefined) {
      queryParams.append("page", String(params.page));
    }
    if (params?.limit !== undefined) {
      queryParams.append("limit", String(params.limit));
    }
    if (params?.mapping_status) {
      queryParams.append("mapping_status", params.mapping_status);
    }
    if (params?.search) {
      queryParams.append("search", params.search);
    }

    const queryString = queryParams.toString();
    const url = `${API_BASE_URL}catalogue/unmapped-products/${csKey}${queryString ? `?${queryString}` : ""}`;
    const response = await ServiceWrapper.doGet(
      url,
      null,
      null,
      AuthManager.getGmsToken()
    );

    if (response?.errorfound) {
      throw new Error(
        response?.data?.message || "Failed to fetch unmapped products"
      );
    }

    return response.data;
  } catch (error: any) {
    throw error;
  }
}

// ============================================================================
// Bulk Action APIs
// ============================================================================

/**
 * Perform Bulk Action (Async)
 *
 * Validates synchronously, then dispatches a Celery task for processing.
 * Returns a task_id for polling via checkJobStatus().
 *
 * @param csKey - Channel source key
 * @param params - Action parameters (skus, action, reason, target_gms_id)
 * @returns Async response with task_id for polling
 */
export async function performBulkAction(
  csKey: string,
  params: BulkActionParams
): Promise<BulkActionAsyncResponse> {
  try {
    if (!csKey) {
      throw new Error("Channel key is required");
    }

    if (!params.skus || params.skus.length === 0) {
      throw new Error("At least one SKU is required");
    }

    if (!params.action) {
      throw new Error("Action is required");
    }

    const url = `${API_BASE_URL}catalogue/data-review/${csKey}/bulk-action`;
    const response = await ServiceWrapper.doPost(
      url,
      params,
      null,
      AuthManager.getGmsToken()
    );

    if (response?.errorfound) {
      throw new Error(
        response?.data?.message || "Failed to perform bulk action"
      );
    }

    return response.data;
  } catch (error: any) {
    throw error;
  }
}

/**
 * Perform Whole-Tab Bulk Action (ticket 13765) — "Update All" / "Create All"
 *
 * Unlike performBulkAction there is NO SKU list: the backend snapshots the
 * action's tab (row count + max _id boundary) and processes every row
 * present at click time via a background parallel run. Fast-return: 202 with
 * queue_uid — poll the Review Queue entry (long-running: hours for large
 * channels, progress visible in the drawer).
 *
 * Requires the dedicated GMSCatalogue:bulkupdate permission (403 otherwise).
 * 409 when a matching run or another bulk-all run is active on the channel.
 *
 * @param csKey - Channel source key
 * @param params - { action: "update" | "create_new", source_tab? }
 * @returns Async response with queue_uid for polling
 */
export async function performBulkAllAction(
  csKey: string,
  params: BulkAllParams
): Promise<BulkActionAsyncResponse> {
  try {
    if (!csKey) {
      throw new Error("Channel key is required");
    }

    if (!params.action) {
      throw new Error("Action is required");
    }

    const url = `${API_BASE_URL}catalogue/data-review/${csKey}/bulk-all`;
    const response = await ServiceWrapper.doPost(
      url,
      params,
      null,
      AuthManager.getGmsToken()
    );

    if (response?.errorfound) {
      throw new Error(
        response?.data?.message || "Failed to start the bulk run"
      );
    }

    return response.data;
  } catch (error: any) {
    throw error;
  }
}

/**
 * Save Bulk Edit Changes
 *
 * Saves edit mode changes for multiple products atomically.
 *
 * @param csKey - Channel source key
 * @param params - Edit parameters with list of product edits
 * @returns Result with success/failure counts
 */
export async function saveBulkEditChanges(
  csKey: string,
  params: BulkEditChangesParams
): Promise<BulkEditChangesResponse> {
  try {
    if (!csKey) {
      throw new Error("Channel key is required");
    }

    if (!params.edits || params.edits.length === 0) {
      throw new Error("At least one edit is required");
    }

    const url = `${API_BASE_URL}catalogue/data-review/${csKey}/bulk-edit-changes`;
    const response = await ServiceWrapper.doPost(
      url,
      params,
      null,
      AuthManager.getGmsToken()
    );

    if (response?.errorfound) {
      throw new Error(
        response?.data?.message || "Failed to save bulk edit changes"
      );
    }

    return response.data;
  } catch (error: any) {
    throw error;
  }
}

// ============================================================================
// Search & Replace APIs
// ============================================================================

/**
 * Search Industry Products
 *
 * Searches for products in the industry catalog for replacement suggestions.
 *
 * @param csKey - Channel source key
 * @param params - Search parameters (q, industry_id, limit)
 * @returns List of matching products
 */
export async function searchProducts(
  csKey: string,
  params: SearchProductsParams
): Promise<SearchProductsResponse> {
  try {
    if (!csKey) {
      throw new Error("Channel key is required");
    }

    if (!params.q || params.q.length < 2) {
      throw new Error("Search query must be at least 2 characters");
    }

    const queryParams = new URLSearchParams();
    queryParams.append("q", params.q);

    if (params.industry_id) {
      queryParams.append("industry_id", params.industry_id);
    }
    if (params.limit !== undefined) {
      queryParams.append("limit", String(params.limit));
    }

    const url = `${API_BASE_URL}catalogue/data-review/${csKey}/search-products?${queryParams.toString()}`;
    const response = await ServiceWrapper.doGet(
      url,
      null,
      null,
      AuthManager.getGmsToken()
    );

    if (response?.errorfound) {
      throw new Error(
        response?.data?.message || "Failed to search products"
      );
    }

    return response.data;
  } catch (error: any) {
    throw error;
  }
}

/**
 * Replace Product Match
 *
 * Replaces the system-suggested product with a user-selected one.
 *
 * @param csKey - Channel source key
 * @param sku - SKU of the product in data review
 * @param params - Replacement parameters (new_gms_id, reason)
 * @returns Result with original and new GMS IDs
 */
export async function replaceProduct(
  csKey: string,
  sku: string,
  params: ReplaceProductParams
): Promise<ReplaceProductResponse> {
  try {
    if (!csKey) {
      throw new Error("Channel key is required");
    }

    if (!sku) {
      throw new Error("SKU is required");
    }

    if (!params.new_gms_id) {
      throw new Error("New GMS ID is required");
    }

    const url = `${API_BASE_URL}catalogue/data-review/${csKey}/products/${encodeURIComponent(sku)}/replace-product`;
    const response = await ServiceWrapper.doPost(
      url,
      params,
      null,
      AuthManager.getGmsToken()
    );

    if (response?.errorfound) {
      throw new Error(
        response?.data?.message || "Failed to replace product"
      );
    }

    return response.data;
  } catch (error: any) {
    throw error;
  }
}

/**
 * Revert Product Replacement
 *
 * Reverts to the original system-suggested product.
 *
 * @param csKey - Channel source key
 * @param sku - SKU of the product in data review
 * @returns Result with original GMS ID
 */
export async function revertReplacement(
  csKey: string,
  sku: string
): Promise<RevertReplacementResponse> {
  try {
    if (!csKey) {
      throw new Error("Channel key is required");
    }

    if (!sku) {
      throw new Error("SKU is required");
    }

    const url = `${API_BASE_URL}catalogue/data-review/${csKey}/products/${encodeURIComponent(sku)}/revert-replacement`;
    const response = await ServiceWrapper.doPost(
      url,
      {},
      null,
      AuthManager.getGmsToken()
    );

    if (response?.errorfound) {
      throw new Error(
        response?.data?.message || "Failed to revert replacement"
      );
    }

    return response.data;
  } catch (error: any) {
    throw error;
  }
}

// ============================================================================
// Create Attribute APIs
// ============================================================================

export interface CreateAttributeParams {
  industry_id: string;
  attribute: string;
  // Optional: the Mapping tabs have no single-category context and omit this,
  // so the backend (data_review_handler::create_attribute_for_category)
  // normalizes missing/empty to "" and skips the Category→Attribute edge.
  category_id?: string;
  description?: string;
  category_name?: string;
  /** AB#14524 "Mark as custom attribute". Optional; backend defaults false, rejects non-booleans (400). */
  is_custom?: boolean;
}

export interface CreateAttributeResponse {
  done: boolean;
  message: string;
  created: boolean;
}

export interface CreateAttributeValuesParams {
  industry_id: string;
  attribute: string;
  attribute_values: string[];
}

export interface CreateAttributeValuesResponse {
  done: boolean;
  message: string;
  created_values: string[];
  existing_values: string[];
}

/**
 * Create Attribute
 *
 * Creates a new attribute and links it to a category.
 * If the attribute already exists, it will be linked to the category.
 *
 * @param csKey - Channel source key
 * @param params - Attribute creation parameters
 * @returns Result with created status
 */
export async function createAttribute(
  csKey: string,
  params: CreateAttributeParams
): Promise<CreateAttributeResponse> {
  try {
    if (!csKey) {
      throw new Error("Channel key is required");
    }

    if (!params.industry_id) {
      throw new Error("Industry ID is required");
    }

    if (!params.attribute) {
      throw new Error("Attribute name is required");
    }

    // category_id is intentionally optional: the Mapping tabs create
    // industry-wide attributes with no single-category context, so the
    // backend skips the Category→Attribute edge when it's missing/empty.
    // The Create-as-New-Product flow always supplies a category_id.

    const url = `${API_BASE_URL}catalogue/data-review/${csKey}/create-attribute`;
    const response = await ServiceWrapper.doPost(
      url,
      params,
      null,
      AuthManager.getGmsToken()
    );

    if (response?.errorfound) {
      throw new Error(
        response?.data?.message || "Failed to create attribute"
      );
    }

    return response.data;
  } catch (error: any) {
    throw error;
  }
}

/**
 * Create Attribute Values
 *
 * Creates new values for an existing attribute.
 *
 * @param csKey - Channel source key
 * @param params - Attribute values creation parameters
 * @returns Result with created and existing values
 */
export async function createAttributeValues(
  csKey: string,
  params: CreateAttributeValuesParams
): Promise<CreateAttributeValuesResponse> {
  try {
    if (!csKey) {
      throw new Error("Channel key is required");
    }

    if (!params.industry_id) {
      throw new Error("Industry ID is required");
    }

    if (!params.attribute) {
      throw new Error("Attribute name is required");
    }

    if (!params.attribute_values || params.attribute_values.length === 0) {
      throw new Error("At least one attribute value is required");
    }

    const url = `${API_BASE_URL}catalogue/data-review/${csKey}/create-attribute-values`;
    const response = await ServiceWrapper.doPost(
      url,
      params,
      null,
      AuthManager.getGmsToken()
    );

    if (response?.errorfound) {
      throw new Error(
        response?.data?.message || "Failed to create attribute values"
      );
    }

    return response.data;
  } catch (error: any) {
    throw error;
  }
}

// ============================================================================
// List Attributes for Industry (Neo4j catalog, channel-scoped via cs_key)
// ============================================================================

export interface DataReviewAttributeItem {
  id: string;
  name: string;
}

export interface DataReviewAttributesResponse {
  done: boolean;
  message?: string;
  data?: {
    items: DataReviewAttributeItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      has_next: boolean;
    };
  };
}

/**
 * Fetch Neo4j catalog Attributes for the industry behind `csKey`. Backs the
 * "Add Attribute" dropdown in the Data Review row-edit modal.
 *
 * Industry-scoped (not category-scoped) so rows without an imported
 * `categoryId` still get a usable list, and attributes that exist on the
 * industry but aren't linked to this row's category aren't silently hidden.
 */
export async function getDataReviewAttributes(
  csKey: string,
  page: number = 1,
  limit: number = 20,
  search: string = ""
): Promise<DataReviewAttributesResponse> {
  if (!csKey) throw new Error("Channel key is required");

  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("limit", String(limit));
  if (search) qs.set("search", search);

  const url = `${API_BASE_URL}catalogue/data-review/${csKey}/attributes?${qs.toString()}`;
  const response = await ServiceWrapper.doGet(
    url,
    null,
    null,
    AuthManager.getGmsToken()
  );

  if (response?.errorfound) {
    throw new Error(response?.data?.message || "Failed to fetch attributes");
  }
  return response.data;
}

// ============================================================================
// List Attribute Values by Name (Neo4j catalog, channel-scoped via cs_key)
// ============================================================================

export interface DataReviewAttributeValueItem {
  id: string;
  value: string;
}

export interface DataReviewAttributeValuesResponse {
  done: boolean;
  message?: string;
  data?: {
    items: DataReviewAttributeValueItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      has_next: boolean;
    };
  };
}

/**
 * Fetch Neo4j catalog AttributeValues for a given Attribute name, scoped to
 * the industry behind `csKey`. Backs the "Add More Values" picker in the
 * Data Review edit-row modal; reads the same Neo4j subgraph that
 * `createAttributeValues` writes to, so newly-created values appear
 * immediately without waiting for any data-mapping pipeline.
 *
 * The attribute name is sent as a query param (not a path segment) so names
 * with slashes/spaces/punctuation round-trip cleanly.
 */
export async function getDataReviewAttributeValues(
  csKey: string,
  attributeName: string,
  page: number = 1,
  limit: number = 20,
  search: string = ""
): Promise<DataReviewAttributeValuesResponse> {
  if (!csKey) throw new Error("Channel key is required");
  if (!attributeName) throw new Error("Attribute name is required");

  const qs = new URLSearchParams();
  qs.set("attribute", attributeName);
  qs.set("page", String(page));
  qs.set("limit", String(limit));
  if (search) qs.set("search", search);

  const url = `${API_BASE_URL}catalogue/data-review/${csKey}/attribute-values?${qs.toString()}`;
  const response = await ServiceWrapper.doGet(
    url,
    null,
    null,
    AuthManager.getGmsToken()
  );

  if (response?.errorfound) {
    throw new Error(response?.data?.message || "Failed to fetch attribute values");
  }
  return response.data;
}

// ============================================================================
// Create Product API
// ============================================================================

export interface CreateProductParams {
  product: {
    name: string;
    model_number: string;
    product_lifecycle_status: string;
    description?: string;
    version?: string;
    upc?: string;
    price?: number;
  };
  category_uid: string;
  parent_id: string;
  real_sku: string;
  attributes?: Array<{
    name: string;
    values: string[];
  }>;
}

export interface CreateProductResponse {
  done: boolean;
  message: string;
  gms_id?: string;
}

/**
 * Create Product from Payload
 *
 * Creates a new product in the catalog from the data review payload.
 * The product is created in Neo4j and SKU service, then moved to processed tab.
 *
 * @param csKey - Channel source key
 * @param sku - SKU of the product in data review
 * @param params - Product creation parameters
 * @returns Result with new GMS ID if successful
 */
export async function createProduct(
  csKey: string,
  sku: string,
  params: CreateProductParams
): Promise<CreateProductResponse> {
  try {
    if (!csKey) {
      throw new Error("Channel key is required");
    }

    if (!sku) {
      throw new Error("SKU is required");
    }

    if (!params.product?.name) {
      throw new Error("Product name is required");
    }

    if (!params.category_uid) {
      throw new Error("Category UID is required");
    }

    const url = `${API_BASE_URL}catalogue/data-review/${csKey}/products/${encodeURIComponent(sku)}/create`;
    const response = await ServiceWrapper.doPost(
      url,
      params,
      null,
      AuthManager.getGmsToken()
    );

    if (response?.errorfound) {
      throw new Error(
        response?.data?.message || "Failed to create product"
      );
    }

    return response.data;
  } catch (error: any) {
    throw error;
  }
}

// ============================================================================
// Review Queue APIs (tickets 11446 + 11447)
// ============================================================================

export type ReviewQueueStatus =
  | "Pending"
  | "In Progress"
  | "Completed"
  | "Failed";

export interface ReviewQueueScope {
  kind: "single" | "bulk" | "mapping";
  label: string;
  identifier: string;
  product_ids: string[];
}

export interface ReviewQueueAffectedCount {
  done: number;
  total: number;
}

export interface ReviewQueueEntry {
  uid: string;
  cs_key: string;
  userid: string;
  username: string;
  party_id: string;
  source_tab: string;
  action: string;
  scope: ReviewQueueScope;
  action_payload: Record<string, unknown>;
  status: ReviewQueueStatus;
  affected_count: ReviewQueueAffectedCount;
  logs: string;
  failure_reason: string;
  retry_count: number;
  task_id: string;
  retry_of: string;
  created_at: number;
  updated_at: number;
  job_start_time: number | null;
  job_end_time: number | null;
  duration: number;
}

export interface ReviewQueueListResponse {
  done: boolean;
  data: ReviewQueueEntry[];
  total?: number;
  message?: string;
}

export interface ReviewQueueRetryResponse {
  done: boolean;
  message: string;
  queue_uid?: string;
  task_id?: string;
  retry_of?: string;
  retry_count?: number;
}

/**
 * List Review Queue entries for the current user on a channel.
 * ``skip`` powers the drawer's infinite scroll — default page size is 25.
 */
export async function fetchReviewQueue(
  csKey: string,
  limit: number = 25,
  skip: number = 0,
): Promise<ReviewQueueListResponse> {
  if (!csKey) throw new Error("Channel key is required");
  const url = `${API_BASE_URL}catalogue/data-review/${csKey}/queue?limit=${encodeURIComponent(
    String(limit),
  )}&skip=${encodeURIComponent(String(skip))}`;
  const response = await ServiceWrapper.doGet(url, null, null, AuthManager.getGmsToken());
  if (response?.errorfound) {
    throw new Error(response?.data?.message || "Failed to fetch review queue");
  }
  return response.data;
}

/**
 * Cheap count fetch for the Review Queue pill badge. Returns just the
 * number of Pending + In Progress rows for the channel — what the FE's
 * 20s background poll uses. Skips wire bandwidth + Mongo row reads
 * compared to fetching the full list and `.length`-filtering on the
 * client.
 */
export async function fetchReviewQueuePendingCount(
  csKey: string,
): Promise<{ done: boolean; pending_count: number; message?: string }> {
  if (!csKey) throw new Error("Channel key is required");
  const url = `${API_BASE_URL}catalogue/data-review/${csKey}/queue/pending-count`;
  const response = await ServiceWrapper.doGet(url, null, null, AuthManager.getGmsToken());
  if (response?.errorfound) {
    throw new Error(response?.data?.message || "Failed to fetch pending count");
  }
  return response.data;
}

/**
 * Fetch a single Review Queue entry by uid. Powers the View Log panel's
 * refresh-logs icon so a per-row refresh doesn't re-pull the whole list.
 */
export async function fetchReviewQueueEntry(
  csKey: string,
  uid: string,
): Promise<{ done: boolean; data: ReviewQueueEntry | null; message?: string }> {
  if (!csKey) throw new Error("Channel key is required");
  if (!uid) throw new Error("Queue entry uid is required");
  const url = `${API_BASE_URL}catalogue/data-review/${csKey}/queue/${encodeURIComponent(uid)}`;
  const response = await ServiceWrapper.doGet(url, null, null, AuthManager.getGmsToken());
  if (response?.errorfound) {
    throw new Error(response?.data?.message || "Failed to fetch queue entry");
  }
  return response.data;
}

/**
 * Retry a failed Review Queue entry. Re-dispatches the original action.
 */
export async function retryReviewQueueItem(
  csKey: string,
  uid: string,
): Promise<ReviewQueueRetryResponse> {
  if (!csKey) throw new Error("Channel key is required");
  if (!uid) throw new Error("Queue entry uid is required");
  const url = `${API_BASE_URL}catalogue/data-review/${csKey}/queue/${encodeURIComponent(uid)}/retry`;
  const response = await ServiceWrapper.doPost(url, {}, null, AuthManager.getGmsToken());
  if (response?.errorfound) {
    throw new Error(response?.data?.message || "Failed to retry queue item");
  }
  return response.data;
}
